import { AnyAction, combineReducers } from 'redux';
import layoutStateReducer from 'layout/store/reducer';
import layoutRendererReducer from 'layoutRenderer/store/reducer';
import loginStateReducer from 'login/store/reducer';
import notificationStateReducer from 'notification/store/reducer';
import { appStateReducer } from 'app/store/reducer';
import { portfolioStateReducer } from 'portfolio/store/reducer';
import { ResetStateAction, RootActionType } from './rootActions';
import State from './State';
import { requestProgressReducer } from 'requestProgress/store/reducer';
import { positionEntryReducer } from 'positionEntry/store/reducer';
import { widgetsReducer } from 'widgets/store/reducer';
import {
    selectAppTimestamp,
    selectBuildVersion,
} from 'app/store/general/selectors';
import { connectivityReducer } from 'app/store/connectivity/connectivityStateSlice';
import { setDisconnected } from 'app/store/connectivity/actions';
import { ConnectivityState } from 'app/store/connectivity/ConnectivityState';
import { embeddedApiReducer } from 'embedded/api/store/reducer';
import { streamsReducer } from 'streamsState/store/reducer';
import embeddedAppReducer from 'embedded/store/embeddedAppStateSlice';
import layoutListReducer from 'layout/store/layoutsListSlice';
import { marketDataSliceReducer } from 'marketData/store/marketDataSlice';
import { bloombergReducer } from 'app/glue/bloomberg/store/bloombergSlice';
import userPreferencesReducer from 'userPreferences/userPreferencesSlice';
import symbolInfoSliceReducer from 'symbols/symbolInfo/symbolInfoSlice';
import symbolStaticDataSliceReducer from 'symbols/store/symbolStaticDataSlice';
import indexMappingsReducer from 'symbols/indexMappings/store/IndexMappingsSlice';
import { selectLayoutPanelId } from 'layout/store/selectors';
import { selectAvailabilityStatusHistory } from 'app/store/connectivity/selectors';
import {
    newportAppReducer,
    selectNewportVersionDetails,
} from 'newport/store/newportAppSlice';
import tradeDatesReducer from 'tradeDates/store/tradeDatesSlice';
import impactModelReducer from 'impactModel/impact-model-slice';
import darkVenueMappingsReducer from 'widgets/darkVenueHeatmap/venueMappings/darkVenueMappingsSlice';

const appReducer = combineReducers<State>({
    app: appStateReducer,
    bloomberg: bloombergReducer,
    darkVenueMappings: darkVenueMappingsReducer,
    embeddedApi: embeddedApiReducer,
    embeddedApp: embeddedAppReducer,
    impactModel: impactModelReducer,
    indexMappings: indexMappingsReducer,
    layout: layoutStateReducer,
    layoutList: layoutListReducer,
    layoutRenderer: layoutRendererReducer,
    login: loginStateReducer,
    marketData: marketDataSliceReducer,
    newportApp: newportAppReducer,
    notification: notificationStateReducer,
    positionEntry: positionEntryReducer,
    portfolio: portfolioStateReducer,
    requestProgress: requestProgressReducer,
    tradeDates: tradeDatesReducer,
    userPreferences: userPreferencesReducer,
    streams: streamsReducer,
    symbolInfos: symbolInfoSliceReducer,
    symbolStaticData: symbolStaticDataSliceReducer,
    widgets: widgetsReducer,
});

function rootReducer(state: State | undefined, action: AnyAction): State {
    if (action.type === RootActionType.RESET_STATE) {
        const nextState = appReducer(undefined, action);
        copyResetPersistentState(action, state, nextState);
        return nextState;
    }
    return appReducer(state, action);
}

export default rootReducer;

function copyResetPersistentState(
    action: ResetStateAction,
    prev: State | undefined,
    next: State
) {
    if (!prev) {
        return;
    }

    const appTimestamp = prev ? selectAppTimestamp(prev) : undefined;
    const requestProgress = prev?.requestProgress;
    const machineId = prev?.app.general.machineId;
    const appMode = prev?.app.general.appMode;
    const embeddedApi = prev?.embeddedApi;
    const embeddedApp = prev?.embeddedApp;
    const shareUsers = prev?.app.shareUsers;
    const bloomberg = prev?.bloomberg;
    const panelId = selectLayoutPanelId(prev);
    const availabilityStatusHistory = selectAvailabilityStatusHistory(prev);
    const newportVersion = selectNewportVersionDetails(prev);
    const serverVersion = selectBuildVersion(prev);

    next.app.general = { ...next.app.general, buildVersion: serverVersion };

    if (appTimestamp) {
        next.app.general.appTimestamp = appTimestamp;
    }

    if (requestProgress) {
        next.requestProgress = requestProgress;
    }

    if (machineId) {
        next.app.general.machineId = machineId;
    }

    if (appMode) {
        next.app.general.appMode = appMode;
    }

    if (panelId) {
        next.layout.panelId = panelId;
    }

    if (embeddedApi) {
        next.embeddedApi = embeddedApi;
    }

    if (embeddedApp) {
        next.embeddedApp = embeddedApp;
    }

    if (shareUsers) {
        next.app.shareUsers = shareUsers;
    }

    if (bloomberg) {
        next.bloomberg = bloomberg;
    }

    if (newportVersion) {
        next.newportApp = {
            ...next.newportApp,
            versionDetails: newportVersion,
        };
    }

    const reduceConnectivity = (action: AnyAction) => {
        next.app.connectivity = connectivityReducer(
            prev.app.connectivity,
            action
        ) as ConnectivityState;
    };

    next.app.connectivity = {
        ...next.app.connectivity,
        availabilityStatus: availabilityStatusHistory,
    };

    const resetStateAction = action as ResetStateAction;
    if (resetStateAction.payload?.networkDownState) {
        reduceConnectivity(
            setDisconnected(
                'Websocket Connection Closed',
                resetStateAction.payload.networkDownState
            )
        );
    } else {
        reduceConnectivity(setDisconnected('Logged Out'));
    }
}
